import './guv.css';
import { useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { t, tn, tx } from '../../i18n';
import { fmtDate, monthName, monthShort } from '../../lib/format';
import { eur, eurS } from '../../lib/money';
import { NoteRow } from '../belege/Bell';
import { useNotifications, useShared } from '../belege/shared';
import type { BwaYear, GuvResponse } from './types';

// „Gewinn und Verlust“ auf der Übersicht (wie in der Demo): Ergebnis seit Januar, Balken je Monat (antippen für
// Umsatz, Ausgaben und Ergebnis), Jahre zum Umschalten, Tabelle. Grundlage ist die BWA, die die Kanzlei hochlädt.

export const useGuv = (clientId: string) => useShared<GuvResponse>(`/api/app/guv/${clientId}`, ['guv', 'clients']);

function useOpenBwa() {
  const navigate = useNavigate();
  return (id: string) => navigate({ search: `?bwa=${id}` }, { state: { sheet: true } });
}

// Meldung „BWA … ist da“, bis sie geöffnet ist
export function GuvNotices() {
  const notes = useNotifications();
  const items = (notes.data?.items ?? []).filter((n) => n.type === 'bwa_uploaded' && !n.read);
  if (!items.length) return null;
  return (
    <div className="notice" role="status">
      {items.map((n) => (
        <NoteRow key={n.id} item={n} />
      ))}
    </div>
  );
}

// „Jan–Aug“
const span = (toMonth: number) => (toMonth === 1 ? monthShort(1) : `${monthShort(1)}–${monthShort(toMonth)}`);

export function GuvCard({ clientId }: { clientId: string }) {
  const res = useGuv(clientId);
  const [year, setYear] = useState<number | null>(null);
  const [sel, setSel] = useState<number | null>(null);
  const d = res.data;
  if (!d) return null;
  const y = d.years.find((x) => x.year === year) ?? d.years[0];

  if (!y) {
    return (
      <div className="card guv-card">
        <h3>{t('Gewinn und Verlust')}</h3>
        <div className="gl">{t('aus der BWA, Ihrem monatlichen Gewinn-Bericht der Kanzlei')}</div>
        <div className="next first">
          {t('Noch keine BWA. Sobald Ihre Kanzlei die erste BWA hochlädt, sehen Sie hier Ihr Ergebnis Monat für Monat.')}
        </div>
      </div>
    );
  }

  // ausgewählter Monat: sonst der letzte mit Einzelwerten
  const lastKnown = y.months.reduce((a, m, i) => (m ? i : a), y.months.length - 1);
  const i = sel !== null && sel < y.months.length ? sel : lastKnown;
  const m = y.months[i];
  const per = span(y.toMonth);
  const known = y.months.flatMap((x) => (x ? [monthName(x.month)] : []));

  return (
    <div className="card guv guv-card">
      <h3>
        {t('Gewinn und Verlust {year}', { year: y.year })} <span>{per}</span>
      </h3>
      <div className="gl">{t('aus der BWA, Ihrem monatlichen Gewinn-Bericht der Kanzlei')}</div>
      {d.years.length > 1 && (
        <div className="ychips" role="group" aria-label={t('Jahr')}>
          {d.years.map((x) => (
            <button
              key={x.year}
              type="button"
              className="chip"
              aria-pressed={x.year === y.year}
              onClick={() => {
                setYear(x.year);
                setSel(null);
              }}
            >
              {x.year}
            </button>
          ))}
        </div>
      )}
      <div className="hero-num">{eurS(y.sum.result)}</div>
      <div className="readout" aria-live="polite">
        {m
          ? y.gap
            ? tn('Ergebnis {period} {sum} · davon {month}: Umsatz {revenue} · Ausgaben {costs} · Ergebnis {result}', {
                period: per,
                sum: eurS(y.sum.result),
                month: monthName(i + 1),
                revenue: eur(m.revenue),
                costs: eur(m.costs),
                result: <b>{eurS(m.result)}</b>,
              })
            : tn('{month}: Umsatz {revenue} · Ausgaben {costs} · Ergebnis {result}', {
                month: monthName(i + 1),
                revenue: eur(m.revenue),
                costs: eur(m.costs),
                result: <b>{eurS(m.result)}</b>,
              })
          : t('{month}: Für diesen Monat enthält die BWA keine Einzelwerte, nur die Summe {period}.', {
              month: monthName(i + 1),
              period: per,
            })}
      </div>
      <Chart key={`${y.year}-${y.id}`} y={y} sel={i} onSelect={setSel} />
      <div className="legend">
        <span>
          <i style={{ background: 'var(--pos)' }} />
          {t('Gewinn')}
        </span>
        <span>
          <i style={{ background: 'var(--neg)' }} />
          {t('Verlust')}
        </span>
        {y.gap && <span>{t('– keine Einzelwerte')}</span>}
        <span>{t('Monat antippen für Details')}</span>
      </div>
      <details className="tbl">
        <summary>{t('Als Tabelle')}</summary>
        <div className="tw">
          <table>
            <thead>
              <tr>
                <th>{t('Monat')}</th>
                <th>{t('Umsatz')}</th>
                <th>{t('Ausgaben')}</th>
                <th>{t('Ergebnis')}</th>
              </tr>
            </thead>
            <tbody>
              {y.months.map((x, k) => (
                <tr key={k}>
                  <td>{monthName(k + 1)}</td>
                  <td>{x ? eur(x.revenue) : '–'}</td>
                  <td>{x ? eur(x.costs) : '–'}</td>
                  <td>{x ? eurS(x.result) : '–'}</td>
                </tr>
              ))}
              <tr>
                <td>
                  <b>{t('Summe {period}', { period: per })}</b>
                </td>
                <td>
                  <b>{eur(y.sum.revenue)}</b>
                </td>
                <td>
                  <b>{eur(y.sum.costs)}</b>
                </td>
                <td>
                  <b>{eurS(y.sum.result)}</b>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </details>
      <div className="src">
        {t(
          'Grundlage: BWA bis {month} {year}, hochgeladen von Ihrer Kanzlei am {date}. Ausgaben = Umsatz minus Ergebnis laut BWA, also inklusive Wareneinsatz. Deshalb können sie von den „Gesamtkosten“ in der PDF abweichen.',
          { month: monthName(y.toMonth), year: y.year, date: fmtDate(y.uploadedAt) },
        )}
        {y.gap &&
          ` ${
            known.length
              ? t('Einzelwerte enthält die BWA nur für {months}, die Summe seit Januar stimmt trotzdem.', {
                  months: known.join(', '),
                })
              : t('Einzelwerte enthält die BWA nur für keinen Monat, die Summe seit Januar stimmt trotzdem.')
          }`}
      </div>
    </div>
  );
}

// Balken: Ergebnis je Monat, Gewinn nach oben, Verlust nach unten; ohne Einzelwerte eine gestrichelte Linie
function Chart({ y, sel, onSelect }: { y: BwaYear; sel: number; onSelect(i: number): void }) {
  const res = y.months.map((m) => (m ? m.result : 0));
  const max = Math.max(...res, 0);
  const min = Math.min(...res, 0);
  const W = 340;
  const H = 170;
  const top = 8;
  const bot = 22;
  const left = 4;
  const right = 4;
  const ph = H - top - bot;
  const range = max - min || 1;
  const y0 = top + (ph * max) / range;
  const slot = (W - left - right) / res.length;
  const bw = Math.min(26, slot - 8);
  const key = (i: number) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(i);
    }
  };

  return (
    <svg className="chart grow" viewBox={`0 0 ${W} ${H}`} role="group" aria-label={t('Ergebnis pro Monat')}>
      <line className="zero" x1="0" x2={W} y1={y0} y2={y0} />
      {y.months.map((m, i) => {
        const x = left + slot * i + (slot - bw) / 2;
        const label = (
          <text
            x={x + bw / 2}
            y={H - 6}
            textAnchor="middle"
            style={i === sel ? { fill: 'var(--ink)', fontWeight: 700 } : m ? undefined : { opacity: 0.6 }}
          >
            {monthShort(i + 1)}
          </text>
        );
        const hit = (
          <rect
            className="hit"
            x={left + slot * i}
            y={0}
            width={slot}
            height={H}
            role="button"
            tabIndex={0}
            aria-pressed={i === sel}
            aria-label={
              m
                ? `${monthName(i + 1)}: ${eurS(m.result)}`
                : t('{month}: keine Einzelwerte in der BWA', { month: monthName(i + 1) })
            }
            onClick={() => onSelect(i)}
            onKeyDown={key(i)}
          />
        );
        if (!m) {
          return (
            <g key={i}>
              <line x1={x} x2={x + bw} y1={y0} y2={y0} stroke="var(--muted)" strokeWidth="2" strokeDasharray="3 3" />
              {label}
              {hit}
            </g>
          );
        }
        const v = m.result;
        const h = Math.max(2, (Math.abs(v) / range) * ph);
        const by = v >= 0 ? y0 - h : y0;
        const r = Math.min(4, h / 2);
        // abgerundete Kante nur am Ende des Balkens
        const path =
          v >= 0
            ? `M${x},${y0}V${by + r}Q${x},${by} ${x + r},${by}H${x + bw - r}Q${x + bw},${by} ${x + bw},${by + r}V${y0}Z`
            : `M${x},${y0}V${y0 + h - r}Q${x},${y0 + h} ${x + r},${y0 + h}H${x + bw - r}Q${x + bw},${y0 + h} ${x + bw},${y0 + h - r}V${y0}Z`;
        return (
          <g key={i}>
            <path
              d={path}
              fill={v >= 0 ? 'var(--pos)' : 'var(--neg)'}
              className={`bar ${v >= 0 ? 'up' : 'down'}${i === sel ? ' sel' : ''}`}
              style={{ animationDelay: `${i * 70}ms` }}
            />
            {label}
            {hit}
          </g>
        );
      })}
    </svg>
  );
}

// Karte „BWA“: die neueste BWA zum Ansehen
export function BwaCard({ clientId }: { clientId: string }) {
  const res = useGuv(clientId);
  const open = useOpenBwa();
  const b = res.data?.latest;
  if (!b) return null;
  return (
    <div className="card guv-card">
      <h3>BWA</h3>
      <div className="gl">{t('Betriebswirtschaftliche Auswertung: Ihr monatlicher Gewinn-Bericht')}</div>
      <div className="ln first">
        <span>
          {tx(b.title)}
          <small>{t('von Ihrer Kanzlei am {date}', { date: fmtDate(b.uploadedAt) })}</small>
        </span>
        <button type="button" className="btn2" onClick={() => open(b.id)}>
          {t('Ansehen')}
        </button>
      </div>
    </div>
  );
}
